import React, { useMemo, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { InputNumber } from 'primereact/inputnumber';
import { SelectButton } from 'primereact/selectbutton';
import { MultiSelect } from 'primereact/multiselect';
import { Chips } from 'primereact/chips';
import { Calendar } from 'primereact/calendar';
import { Button } from '../../../components/ui/Button';
import { Message } from 'primereact/message';

import WizardShell from '../../Cafe/wizard/WizardShell';
import { useCustomToast } from '../../Contexts/ToastContext';
import { useCreateCampaign, useUpdateCampaign, usePreflight, useSendTest } from '../../../queries/marketing';

const SOURCE_KEYS = ['birthday_event', 'spin_win', 'happy_hour', 'manual', 'import'];

/**
 * One-shot marketing campaign wizard: Content → Audience → Schedule → Review.
 * Presentation on the shared WizardShell. Creates a draft on first save/test,
 * then finalizes (schedules) on submit. Fully i18n (EN/AR); the email body is
 * venue-authored with {name}/{venue} placeholders and an Auto/RTL/LTR toggle.
 */
export default function CampaignWizard({ onExit, initialSegment = null }) {
  const { t } = useTranslation();
  const { showToast } = useCustomToast();
  const companyDetails = useSelector((s) => s.global.companyDetails);
  const hasLogo = !!companyDetails?.company?.logo;

  const [stepIndex, setStepIndex] = useState(0);
  const [draftId, setDraftId] = useState(null);

  // Content
  const [name, setName] = useState('');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [direction, setDirection] = useState('auto');
  const [ctaLabel, setCtaLabel] = useState('');
  const [ctaUrl, setCtaUrl] = useState('');

  // Audience
  const [segment, setSegment] = useState(initialSegment);   // badge-driven segment (from "Message these customers")
  const [sources, setSources] = useState([]);
  const [tags, setTags] = useState([]);
  const [inactiveDays, setInactiveDays] = useState(null);

  // Schedule
  const [sendMode, setSendMode] = useState('now');   // now | later
  const [scheduledAt, setScheduledAt] = useState(null);

  const [preflight, setPreflight] = useState(null);

  const create = useCreateCampaign();
  const update = useUpdateCampaign();
  const preflightMut = usePreflight();
  const testMut = useSendTest();

  const directionOptions = [
    { label: t('marketing.dir_auto'), value: 'auto' },
    { label: t('marketing.dir_rtl'), value: 'rtl' },
    { label: t('marketing.dir_ltr'), value: 'ltr' },
  ];
  const sendModeOptions = [
    { label: t('marketing.send_now'), value: 'now' },
    { label: t('marketing.send_later'), value: 'later' },
  ];
  const sourceOptions = SOURCE_KEYS.map((k) => ({ label: t(`customers.source.${k}`), value: k }));

  const targetFilter = useMemo(() => {
    const f = {};
    if (segment) f.segment = segment;
    if (sources.length) f.sources = sources;
    if (tags.length) f.tags = tags;
    if (inactiveDays) f.inactive_days = inactiveDays;
    return f;
  }, [segment, sources, tags, inactiveDays]);

  const payload = useCallback((mode) => ({
    name: name.trim(),
    template_subject: subject.trim(),
    template_body: body.trim(),
    cta_label: ctaLabel.trim() || null,
    cta_url: ctaUrl.trim() || null,
    direction,
    max_per_customer: 1,
    target_filter: targetFilter,
    send_mode: mode,
    ...(mode === 'later' && scheduledAt ? { scheduled_at: new Date(scheduledAt).toISOString() } : {}),
  }), [name, subject, body, ctaLabel, ctaUrl, direction, targetFilter, scheduledAt]);

  const runPreflight = useCallback(async () => {
    try {
      const res = await preflightMut.mutateAsync({ channel: 'email', target_filter: targetFilter });
      setPreflight(res);
    } catch (_) { /* toast handled globally */ }
  }, [preflightMut, targetFilter]);

  // Ensure a draft exists (for the test send / incremental save).
  const ensureDraft = useCallback(async () => {
    if (draftId) {
      await update.mutateAsync({ id: draftId, ...payload('draft') });
      return draftId;
    }
    const created = await create.mutateAsync(payload('draft'));
    setDraftId(created.id);
    return created.id;
  }, [draftId, update, create, payload]);

  const insertToken = (token) => setBody((b) => (b ? `${b} ${token}` : token));

  const sendTest = async () => {
    if (!subject.trim() || !body.trim()) {
      showToast('warn', 'Marketing', t('marketing.fill_content_first'));
      return;
    }
    const id = await ensureDraft();
    const res = await testMut.mutateAsync(id);
    if (res?.sent) showToast('success', 'Marketing', t('marketing.test_sent', { to: res.to }));
    else showToast('error', 'Marketing', t('marketing.test_failed'));
  };

  const steps = [
    t('marketing.step_content'),
    t('marketing.step_audience'),
    t('marketing.step_schedule'),
    t('marketing.step_review'),
  ];

  const canNext = useMemo(() => {
    if (stepIndex === 0) return name.trim() && subject.trim() && body.trim();
    if (stepIndex === 2) return sendMode === 'later' ? !!scheduledAt : true;
    return true;
  }, [stepIndex, name, subject, body, sendMode, scheduledAt]);

  const submitting = create.isLoading || update.isLoading;

  const onBack = () => (stepIndex === 0 ? onExit?.() : setStepIndex((i) => i - 1));

  const onNext = async () => {
    // Refresh audience/quota math when arriving on Audience or Review.
    if (stepIndex === 0 || stepIndex === 1) await runPreflight();

    if (stepIndex < steps.length - 1) {
      setStepIndex((i) => i + 1);
      return;
    }

    // Final: create or finalize the campaign with the chosen schedule.
    try {
      const mode = sendMode; // now | later
      if (draftId) await update.mutateAsync({ id: draftId, ...payload(mode) });
      else await create.mutateAsync(payload(mode));
      showToast('success', 'Marketing', mode === 'now' ? t('marketing.scheduled_now') : t('marketing.scheduled_later'));
      onExit?.();
    } catch (_) { /* toast handled globally */ }
  };

  return (
    <WizardShell
      icon="📣"
      title={t('marketing.new_campaign')}
      subtitle={t('marketing.wizard_subtitle')}
      steps={steps}
      stepIndex={stepIndex}
      canNext={canNext}
      submitting={submitting}
      nextLabel={sendMode === 'later' ? t('marketing.schedule_btn') : t('marketing.send_btn')}
      onBack={onBack}
      onNext={onNext}
    >
      {/* ── Step 1: Content ─────────────────────────────────────────────── */}
      {stepIndex === 0 && (
        <div className="flex flex-column gap-3">
          <div>
            <label className="block mb-1 font-medium">{t('marketing.field_name')}</label>
            <InputText value={name} onChange={(e) => setName(e.target.value)} className="w-full"
              placeholder={t('marketing.field_name_ph')} />
          </div>
          <div>
            <label className="block mb-1 font-medium">{t('marketing.field_subject')}</label>
            <InputText value={subject} onChange={(e) => setSubject(e.target.value)} className="w-full"
              placeholder={t('marketing.field_subject_ph')} />
          </div>
          <div>
            <div className="flex align-items-center justify-content-between mb-1">
              <label className="font-medium">{t('marketing.field_body')}</label>
              <div className="flex gap-1">
                <Button type="button" size="small" outlined label="{name}" onClick={() => insertToken('{name}')} />
                <Button type="button" size="small" outlined label="{venue}" onClick={() => insertToken('{venue}')} />
              </div>
            </div>
            <InputTextarea value={body} onChange={(e) => setBody(e.target.value)} rows={7} autoResize
              className="w-full" dir={direction === 'rtl' ? 'rtl' : direction === 'ltr' ? 'ltr' : 'auto'}
              placeholder={t('marketing.field_body_ph')} />
            <small className="text-500">{t('marketing.body_hint')}</small>
          </div>
          <div>
            <label className="block mb-1 font-medium">{t('marketing.field_direction')}</label>
            <SelectButton value={direction} onChange={(e) => e.value && setDirection(e.value)} options={directionOptions} />
          </div>
          <div className="grid">
            <div className="col-12 md:col-6">
              <label className="block mb-1 font-medium">{t('marketing.field_cta_label')}</label>
              <InputText value={ctaLabel} onChange={(e) => setCtaLabel(e.target.value)} className="w-full"
                placeholder={t('marketing.field_cta_label_ph')} />
            </div>
            <div className="col-12 md:col-6">
              <label className="block mb-1 font-medium">{t('marketing.field_cta_url')}</label>
              <InputText value={ctaUrl} onChange={(e) => setCtaUrl(e.target.value)} className="w-full" dir="ltr"
                placeholder="https://wa.me/9665… / https://…" />
            </div>
          </div>
          <div>
            <Button type="button" outlined icon="pi pi-send" loading={testMut.isLoading || ensureDraftLoading(create, update)}
              label={t('marketing.send_test')} onClick={sendTest} />
            <small className="block text-500 mt-1">{t('marketing.send_test_hint')}</small>
          </div>
        </div>
      )}

      {/* ── Step 2: Audience ────────────────────────────────────────────── */}
      {stepIndex === 1 && (
        <div className="flex flex-column gap-3">
          {segment && (
            <div className="flex align-items-center gap-2 p-2 border-round"
              style={{ background: 'var(--highlight-bg, #fff3e0)', border: '1px solid var(--surface-300)' }}>
              <i className="pi pi-filter" />
              <span className="font-semibold">{t(`customers.segment.${segment}`)}</span>
              <span className="text-500 text-sm">{t('marketing.aud_segment_note')}</span>
              <Button icon="pi pi-times" text size="small" className="ml-auto" onClick={() => setSegment(null)} aria-label={t('marketing.aud_segment_clear')} />
            </div>
          )}
          <div>
            <label className="block mb-1 font-medium">{t('marketing.aud_sources')}</label>
            <MultiSelect value={sources} onChange={(e) => setSources(e.value)} options={sourceOptions}
              display="chip" placeholder={t('marketing.aud_sources_all')} className="w-full" />
          </div>
          <div>
            <label className="block mb-1 font-medium">{t('marketing.aud_tags')}</label>
            <Chips value={tags} onChange={(e) => setTags(e.value)} className="w-full" separator=","
              placeholder={t('marketing.aud_tags_ph')} />
          </div>
          <div>
            <label className="block mb-1 font-medium">{t('marketing.aud_inactive')}</label>
            <div className="flex align-items-center gap-2">
              <InputNumber value={inactiveDays} onValueChange={(e) => setInactiveDays(e.value)} min={1} max={3650}
                showButtons style={{ width: '9rem' }} placeholder="—" />
              <span className="text-500">{t('marketing.aud_inactive_unit')}</span>
            </div>
            <small className="text-500">{t('marketing.aud_inactive_hint')}</small>
          </div>

          <Button type="button" outlined icon="pi pi-refresh" label={t('marketing.aud_recount')}
            loading={preflightMut.isLoading} onClick={runPreflight} className="align-self-start" />

          {preflight && (
            <div className="surface-100 border-round p-3">
              <div className="flex justify-content-between"><span>{t('marketing.aud_matched')}</span><b>{preflight.audience_matched}</b></div>
              <div className="flex justify-content-between"><span>{t('marketing.aud_eligible')}</span><b>{preflight.eligible}</b></div>
              <small className="text-500">{t('marketing.aud_eligible_hint')}</small>
            </div>
          )}
        </div>
      )}

      {/* ── Step 3: Schedule ────────────────────────────────────────────── */}
      {stepIndex === 2 && (
        <div className="flex flex-column gap-3">
          <div>
            <label className="block mb-2 font-medium">{t('marketing.when')}</label>
            <SelectButton value={sendMode} onChange={(e) => e.value && setSendMode(e.value)} options={sendModeOptions} />
          </div>
          {sendMode === 'later' && (
            <div>
              <label className="block mb-1 font-medium">{t('marketing.pick_datetime')}</label>
              <Calendar value={scheduledAt} onChange={(e) => setScheduledAt(e.value)} showTime hourFormat="24"
                showIcon minDate={new Date()} className="w-full" dateFormat="yy-mm-dd" />
            </div>
          )}
        </div>
      )}

      {/* ── Step 4: Review ──────────────────────────────────────────────── */}
      {stepIndex === 3 && (
        <div className="flex flex-column gap-3">
          {!hasLogo && (
            <Message severity="info" className="w-full" content={
              <span>
                {t('marketing.logo_nudge')}{' '}
                <Link to="/profile">{t('marketing.logo_nudge_link')}</Link>
              </span>
            } />
          )}

          <div className="surface-100 border-round p-3">
            <div className="flex justify-content-between mb-1"><span>{t('marketing.review_name')}</span><b>{name}</b></div>
            <div className="flex justify-content-between mb-1"><span>{t('marketing.review_subject')}</span><b className="text-right">{subject}</b></div>
            <div className="flex justify-content-between"><span>{t('marketing.review_when')}</span>
              <b>{sendMode === 'now' ? t('marketing.send_now') : (scheduledAt ? new Date(scheduledAt).toLocaleString() : '—')}</b>
            </div>
          </div>

          {preflight ? (
            <>
              {preflight.over_quota ? (
                <Message severity="warn" className="w-full" content={
                  <div>
                    <div className="font-bold text-lg mb-1">
                      {t('marketing.will_send_of', { will: preflight.will_send, eligible: preflight.eligible })}
                    </div>
                    <div className="font-semibold" style={{ color: '#b45309' }}>
                      {t('marketing.shortfall_warn', { n: preflight.shortfall })}
                    </div>
                    <div className="text-700 mt-1">{t('marketing.upgrade_to_reach')}</div>
                  </div>
                } />
              ) : (
                <Message severity="success" className="w-full" content={
                  <div className="font-bold text-lg">
                    {preflight.unlimited
                      ? t('marketing.will_send_all', { n: preflight.eligible })
                      : t('marketing.will_send_of', { will: preflight.will_send, eligible: preflight.eligible })}
                  </div>
                } />
              )}
            </>
          ) : (
            <Button type="button" outlined icon="pi pi-refresh" label={t('marketing.aud_recount')}
              loading={preflightMut.isLoading} onClick={runPreflight} className="align-self-start" />
          )}
        </div>
      )}
    </WizardShell>
  );
}

/** Small helper so the test button spins while a draft is being persisted. */
function ensureDraftLoading(create, update) {
  return create.isLoading || update.isLoading;
}
